import { makeStyles } from '@mui/styles';
import MenuItem from 'apollo-react/components/MenuItem';
import Modal from 'apollo-react/components/Modal';
import Radio from 'apollo-react/components/Radio';
import RadioGroup from 'apollo-react/components/RadioGroup';
import Select from 'apollo-react/components/Select';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';

import {
  clearReportCreationData,
  fetchDatasetList,
  fetchReportsList,
  setCreateReportModalOpen,
} from '../../actions/PowerBIActions';
import { CREATE_REPORT_OPTIONS } from '../../constants/PowerBIConstants';
import { POWERBI_EDITOR_PAGE } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import {
  getCreateReportData,
  getDatasetList,
  getReportsList,
} from '../../selectors/PowerBISelectors';
import { filterNonNull, validateReportCreationForm } from '../../utils/powerBIUtils';

const useStyles = makeStyles({
  modal: {
    minWidth: '35%',
    zIndex: 1200,
  },
  radioButtonContainer: {
    flexWrap: 'nowrap',
    maxHeight: 380,
    overflowY: 'auto',
    paddingLeft: 1,
  },
});

const initialReportSettings = {
  reportType: 'new',
  datasetId: '',
  reportId: '',
  isSubmitted: false,
};

const initialValidationErrors = {
  datasetId: { hasError: false, errorText: '' },
  reportId: { hasError: false, errorText: '' },
};

const CreateReportModal = () => {
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();

  const { itmId, reportName, entityName } = useSelector(getCreateReportData);
  const datasetList = useSelector(getDatasetList);
  const reportsList = useSelector(getReportsList);

  const [reportSettings, setReportSettings] = useState(initialReportSettings);
  const [validationErrors, setValidationErrors] = useState(initialValidationErrors);

  const hasValidationErrors =
    Object.values(validationErrors).filter((item) => item?.hasError).length !== 0;

  useEffect(() => {
    if (!datasetList.length) {
      dispatch(fetchDatasetList());
    }

    return () => {
      dispatch(clearReportCreationData());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reportSettings.reportType === 'existing' && !reportsList.length) {
      dispatch(fetchReportsList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSettings.reportType]);

  const handleFormSubmit = () => {
    setReportSettings({ ...reportSettings, isSubmitted: true });

    const errors = validateReportCreationForm(reportSettings);

    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return;
    }

    dispatch(setCreateReportModalOpen(false));

    const createReportQueryObject = {
      reportName,
      itmId,
      entityName,
      datasetId: reportSettings.datasetId,
      reportId: reportSettings.reportId,
    };

    const link = `${POWERBI_EDITOR_PAGE}?${qs.stringify(filterNonNull(createReportQueryObject), {
      skipNulls: true,
    })}`;

    history.push(link);
  };

  const handleSettingChange = (e) => {
    const settingName = e.target.name;
    const settingValue = e.target.value;

    if (settingName === 'reportType') {
      setReportSettings({
        ...initialReportSettings,
        isSubmitted: reportSettings.isSubmitted,
        reportType: settingValue,
      });
      setValidationErrors(initialValidationErrors);
    } else {
      setReportSettings({ ...reportSettings, [settingName]: settingValue });
    }
  };

  const handleSettingBlur = () => {
    if (reportSettings.isSubmitted) {
      const errors = validateReportCreationForm(reportSettings);
      setValidationErrors(errors);
    }
  };

  const buttonProps = [
    {
      label: translateString('Cancel'),
    },
    {
      label: translateString('Create'),
      onClick: handleFormSubmit,
      disabled: hasValidationErrors,
    },
  ];

  return (
    <Modal
      onClose={() => dispatch(setCreateReportModalOpen(false))}
      open
      className={classes.modal}
      title={translateString('Edit Report')}
      buttonProps={buttonProps}
    >
      <div style={{ marginTop: 5, marginBottom: 10 }}>
        <RadioGroup
          name="reportType"
          row
          required
          value={reportSettings.reportType}
          onChange={handleSettingChange}
          label={translateString(
            'Do you want to create a brand new Report or create based on existing one?'
          )}
        >
          {CREATE_REPORT_OPTIONS.map(({ value, label }) => (
            <Radio key={value} value={value} label={translateString(label)} />
          ))}
        </RadioGroup>
        {reportSettings.reportType === 'new' && (
          <Select
            value={reportSettings.datasetId}
            onChange={handleSettingChange}
            name="datasetId"
            canDeselect={false}
            fullWidth
            label={translateString('Select Dataset')}
            required
            error={validationErrors?.datasetId?.hasError}
            onBlur={handleSettingBlur}
            disabled={!datasetList.length}
            helperText={
              validationErrors?.datasetId?.hasError
                ? translateString(validationErrors?.datasetId?.errorText)
                : null
            }
          >
            {datasetList.map(({ datasetName, datasetId }) => (
              <MenuItem value={datasetId} key={datasetId}>
                {datasetName}
              </MenuItem>
            ))}
          </Select>
        )}
        {reportSettings.reportType === 'existing' && (
          <Select
            value={reportSettings.reportId}
            onChange={handleSettingChange}
            name="reportId"
            canDeselect={false}
            fullWidth
            label={translateString('Select Report')}
            required
            disabled={!reportsList.length}
            error={validationErrors?.reportId?.hasError}
            onBlur={handleSettingBlur}
            helperText={
              validationErrors?.reportId?.hasError
                ? translateString(validationErrors?.reportId?.errorText)
                : null
            }
          >
            {reportsList.map(({ reportName, reportId }) => (
              <MenuItem value={reportId} key={reportId}>
                {reportName}
              </MenuItem>
            ))}
          </Select>
        )}
      </div>
    </Modal>
  );
};

export default CreateReportModal;
